import { findInMessagesByName, hasProperty } from '../helpers';
import { toUrlFormat } from '..';
import { NODE_TYPES } from '../../typeUtils';

export const serviceDefaultDataMembers = (
  name,
  urlFormat,
  isArticlesCategory,
  tagSortOrder
) => {
  const getAdditionalPropsForApiMembers = () => ({
    mixes: [],
    labels: [],
    events: [],
    memberOf: '',
    docs: { summary: '', description: [] },
    properties: [],
    entities: [],
    operations: [],
    callbacks: [],
    messages: [],
    extra: {},
  });

  const getAdditionalPropsForArticlesSectionTagMembers = () => ({
    sortOrder: tagSortOrder,
  });

  const getAdditionalPropsByType = () =>
    isArticlesCategory
      ? getAdditionalPropsForArticlesSectionTagMembers()
      : getAdditionalPropsForApiMembers();

  return {
    name,
    relatedGuides: [],
    urlFormat,
    ...getAdditionalPropsByType(),
  };
};

const createSubTreeKeysWithDefaultData = (node) =>
  node.data.tags.forEach((tag) => {
    const urlFormat = toUrlFormat(tag.name);
    if (!node[`/${urlFormat}`]) {
      node[`/${urlFormat}`] = {
        type: NODE_TYPES.TAG,
        data: serviceDefaultDataMembers(tag.name, urlFormat),
      };
    }
  });

const RemoveFromCurrentNode = (node, _prop) =>
  (node.data[_prop] = node.data[_prop].filter(Boolean));

const populateTagsSubTreeWithMembers = (node) => {
  ['operations', 'messages'].forEach((_prop) => {
    if (node.data[_prop]) {
      node.data[_prop].forEach((member, index) => {
        if (member.tag && node[constructKey(member.tag)]) {
          node[constructKey(member.tag)].data[_prop].push(member);
          node.data[_prop][index] = false;
        }
      });
      RemoveFromCurrentNode(node, _prop);
    }
  });
};

const addWebhooksEventsToSubTrees = (node) => {
  if (node.data.events && node.data.events.length > 0) {
    node.data.tags.forEach((tag) => {
      node.data.events.forEach((event, i) => {
        if (event.tag === tag.name) {
          node.data.events[i] = false;
          node[constructKey(tag.name)].data.events.push(event);
          const eventObject = findInMessagesByName(node)(event.type);
          if (eventObject) {
            node[constructKey(tag.name)].data.messages.push({ ...eventObject });
            eventObject.ignore = true;
          }
        }
      });
      node.data.events = node.data.events.filter(Boolean);
    });
  }
};

const getTags = (node) => {
  return node.data.tags || [];
};

const addMainEntityToSubTreeNode = (node, tagMainEntity) => {
  node.data.messages.push({ ...tagMainEntity, isMainEntity: true });
  node.data.entities.push({
    type: tagMainEntity.name,
  });
};

export const addMainEntityToSubTrees = (node) => {
  getTags(node).forEach((tag) => {
    const subTreeKey = constructKey(tag.name);
    const tagMainEntity = findInMessagesByName(node)(tag.entity);
    if (tagMainEntity) {
      addMainEntityToSubTreeNode(node[subTreeKey], tagMainEntity);
    }
  });
  node.data.entities = [];
};

const constructKey = (str) => `/${toUrlFormat(str)}`;

const pushTagMainEntityToContainingNode = (node) => {
  const entity = node?.data?.tags?.[0]?.entity;
  if (entity) {
    if (node?.data?.entities) {
      node.data.entities.push({ type: entity });
    } else {
      node.data.entities = [{ type: entity }];
    }
  }
};

const hasTagsInGuides = ({ guides = [], relatedGuides = [], tags = [] }) => {
  const nodeTagsMap = {};
  tags.forEach(({ name }) => {
    nodeTagsMap[toUrlFormat(name)] = true;
  });
  const isUniqueTag = ({ tag }) => tag && !nodeTagsMap[toUrlFormat(tag)];
  return guides.some(isUniqueTag) || relatedGuides.some(isUniqueTag);
};

const hasMoreThanOneTag = (node) => {
  if (node?.data?.tags) {
    return (
      node.data.tags.length > 1 ||
      (node.data.tags.length > 0 && hasTagsInGuides(node.data))
    );
  }
};

const forceAddSingleTag = (node) =>
  hasProperty(node, 'tags') && node.data.tags.some((tag) => tag.forceTag);

export const createTagsSubTreesVisitor = (node) => {
  /*
    if the node has more than one tag:
    1. we create a new entries (tags) in the node.
    2. move the entities from the node to the relevant new entry (tag).
    3. move the events from the node to the relevant new entry (tag).
    4. move the operations & messages from the node to the relevant new entry (tag).

    else we just push the first tag entity if has any, to the node entities
  */
  if (hasMoreThanOneTag(node) || forceAddSingleTag(node)) {
    createSubTreeKeysWithDefaultData(node);
    addMainEntityToSubTrees(node);
    addWebhooksEventsToSubTrees(node);
    populateTagsSubTreeWithMembers(node);
  } else {
    pushTagMainEntityToContainingNode(node);
  }
};
